import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FetcherWithComponents } from 'react-router';

import InformationIcon from '~/assets/icons/Information';
import { Button } from '~/components/Button';
import type { NotificationData } from '~/components/ProductNotificationButton';
import SignUpMethodEvent from '~/events/SignUpMethodEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { buildNotificationProducts } from '~/utils/browser';
import { getPageViewId } from '~/utils/navigationContext';

import { authButtonClassName, authInputClassName } from '../AuthDialog';
import AuthPanel from '../AuthPanel';
import TermsNotice from '../TermsNotice';
import type { FetcherData } from '.';

type Props = {
  fetcher: FetcherWithComponents<FetcherData>;
  product: NotificationData;
  sizes: string[];
};

export default function EmailEntry({ fetcher, product, sizes }: Props) {
  return (
    <>
      <EmailInput product={product} fetcher={fetcher} sizes={sizes} />
      <TermsNotice />
    </>
  );
}

function EmailInput({ fetcher, product, sizes }: Props) {
  const [email, setEmail] = useState('');
  const user = useUser();
  const { isBot } = useRootData();
  const { t } = useTranslation();

  const products = buildNotificationProducts(product?.id, sizes);

  return (
    <fetcher.Form className="w-full" action="/bolt/register" method="post">
      <input type="hidden" value={product?.legacyId} name="legacyProductId" />
      <input type="hidden" value={JSON.stringify(products)} name="products" />
      <input type="hidden" value={user.guestId} name="guestId" />
      <input type="hidden" value={getPageViewId() ?? ''} name="viewId" />
      <input type="hidden" value="restock" name="action" />
      <AuthPanel>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={authInputClassName}
          placeholder={t('login.emailAddress')}
          name="email"
        />
        {email && (
          <Button
            type="submit"
            variant="forest"
            className={authButtonClassName}
            onClick={() =>
              new SignUpMethodEvent({
                buttonName: 'SIGN_UP_EMAIL',
                isBot,
                pagePosition: 'Sign in modal',
                tempContext: 'Notification Flow'
              }).send()
            }
          >
            Next
          </Button>
        )}
        {fetcher.data?.errors?.conflict && (
          <span className="flex w-full flex-row items-center justify-center gap-2 text-xs text-red-alert">
            <InformationIcon circleStroke="#D71225" fill="#D71225" />
            Email already in use, please go back to log in or reset your
            password
          </span>
        )}
      </AuthPanel>
    </fetcher.Form>
  );
}
